import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { useEffect, useState } from 'react'

import LoadingSpinner from '@/components/TailwindUI/LoadingSpinner'
import cn from '@/utilities/cn'

export type Option = {
  label: string
  value: string | number
}

export type ListBoxProps = {
  className?: string
  error?: string | boolean
  name: string
  label?: string
  type?: string
  disabled?: boolean
  boldHeaders?: boolean
  options: Option[]
  value: string | number | undefined
  onChange: (option: Option) => void
  required?: boolean
  description?: string
  loading?: boolean
  placeholder?: string
  includeTransition?: boolean
}
const ListBox = ({
  error,
  className,
  label,
  name,
  required,
  options,
  boldHeaders = false,
  onChange,
  disabled,
  description,
  loading,
  value,
  placeholder,
  includeTransition = true
}: ListBoxProps) => {
  const [selected, setSelected] = useState(options.find(option => option.value === value))
  const onChangeOption = (option: Option) => {
    setSelected(option)
    onChange(option)
  }

  useEffect(() => {
    setSelected(options.find(option => option.value === value))
  }, [options, value])
  const listboxOptions = (
    <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
      {options.map(option => (
        <ListboxOption
          key={option?.value}
          className={({ focus }) =>
            cn(
              focus ? 'bg-brand-400 text-white' : 'text-zinc-900',
              'relative cursor-default select-none py-2 pl-3 pr-9'
            )
          }
          value={option}
        >
          {({ selected, focus }) => (
            <>
              <span className={cn(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>{option.label}</span>

              {selected && (
                <span
                  className={cn(
                    focus ? 'text-white' : 'text-brand-400',
                    'absolute inset-y-0 right-0 flex items-center pr-4'
                  )}
                >
                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                </span>
              )}
            </>
          )}
        </ListboxOption>
      ))}
    </ListboxOptions>
  )
  return (
    <div className={className}>
      <Listbox disabled={disabled || loading} value={selected} onChange={onChangeOption}>
        {({ open }) => (
          <>
            <Label
              htmlFor={name}
              className={cn('block text-sm font-medium leading-6 text-zinc-900', { 'font-bold': boldHeaders })}
            >
              {label}
              {required && <span className="ml-1 text-red-500">*</span>}
            </Label>

            {description && <span className="text-sm text-zinc-500">{description}</span>}
            <div className="relative mt-2">
              <ListboxButton
                className={cn(
                  'sm:leading-6relative w-full cursor-default rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-left text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-400 sm:text-sm sm:leading-6',
                  error ? 'ring-red-300 focus:ring-red-600' : 'ring-zinc-300 focus:ring-brand-400',
                  disabled || loading ? 'bg-zinc-50' : ''
                )}
              >
                <span className="block truncate">
                  {selected?.label || <span className="text-zinc-500">{placeholder}</span>}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  {loading ? (
                    <LoadingSpinner className="h-5 w-5 text-zinc-400" />
                  ) : (
                    <ChevronUpDownIcon className="h-5 w-5 text-zinc-400" aria-hidden="true" />
                  )}
                </span>
              </ListboxButton>
              {includeTransition ? (
                <Transition
                  show={open}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  {listboxOptions}
                </Transition>
              ) : (
                listboxOptions
              )}
            </div>
          </>
        )}
      </Listbox>
    </div>
  )
}
export default ListBox
